import { Baseline, EMPackEntry, EntityModificationPack, GenericException, Scenario, ScenarioParameters, ScenarioStepParameter } from '@wizeoo/akm-message-lib';
import { ScenarioVO } from '../../vo/scenarii/scenario.vo';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { WizIcon, WizPopUp, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { cloneDeep } from 'lodash-es';
import { TranslatableLabelEntryVO } from '../../vo/common/translation/translatable-label-entry.vo';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { ScenarioEngine as BaseScenarioEngine, WidgetLine, WidgetCell, FeiKeyDef, EM_ACTION, VisionVO } from '@wizeoo/akm-compute-lib';
import { StyleEngine } from '../workingdata/style.engine';
import { WizColDef } from '../../manager/workingdata/fei.engine';


// Structure de données d'une colonne de l'ag-grid
export interface scenCellConfig {
  useScen?: boolean; // mode scénario actif
  color?: string; // couleur du scenario
  hasDefault?: boolean; // le scenario MAIN est présent dans le pack
  isScen?: boolean; // un scenar est actif
}


/**
 * Manager des scenarii
 */
export class ScenarioEngine extends BaseScenarioEngine {
 
  constructor(model: Model) {
    super(model);
  }

  public get model(): Model {
    return this._model as Model;
  }
  public set model(value: Model) {
    this._model = value;
  }

    /* ################################################
        le constructeur (pour obtenir le bon type)
    ################################################ */
  public override newScenarioVO(source?: Scenario): ScenarioVO {
    return new ScenarioVO(this._model as Model, source);
  }

  public override getScenario(visionCode: string, scenCode: string): ScenarioVO {
    return super.getScenario(visionCode, scenCode) as ScenarioVO;
  }

  public override getScenarioByVision(visionCode: string): ScenarioVO[] {
    return super.getScenarioByVision(visionCode) as ScenarioVO[];
  }

  public override getScenarioMapByVision(visionCode: string): Map<string, ScenarioVO> {
    return super.getScenarioMapByVision(visionCode) as Map<string, ScenarioVO>;
  }

  public override getScenarioByVisionOrderer(visionCode: string): ScenarioVO[] {
    return super.getScenarioByVisionOrderer(visionCode) as ScenarioVO[];
  }

  /**
   * Clone d'un scen pour édition
   * @param sty le scen à cloner
   * @return scenVO,  Un clone
   */
  public clone(value: ScenarioVO): ScenarioVO {
    const clone = cloneDeep(value);
    return clone;
  }

  /**
   * create/ update d'une pile de scenarii
   * @param to les clones en cours de modif
   * @param from les existants, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: ScenarioVO[], from: ScenarioVO[], callback: Function): void {
    let pack: EntityModificationPack = null;
    
    let toUniqueMap: Map<string, ScenarioVO> = new Map(); // to.map(scen => [scen.uniqueKey, scen]));
    to.forEach(scen => {
      if(scen.code !== ViewData.DEFAULT_SCENARIO) { // on va pas gérer les MAIN
        toUniqueMap.set(scen.code, scen);
      }
    });

    let fromUniqueMap: Map<string, ScenarioVO> =  new Map(); // from.map(scen => [scen.uniqueKey, scen]));
    from.forEach(scen => {
      if(scen.code !== ViewData.DEFAULT_SCENARIO) { // on va pas gérer les MAIN
        fromUniqueMap.set(scen.code, scen);
      }
    });

    let updatePile: ScenarioVO[] = []; // on va stocker ici les sources

    
    [...fromUniqueMap.values()].forEach(existVO => {
      if(!toUniqueMap.has(existVO.code)) { // on supprime les deleteds (pas trouvés dans to)
        existVO.operation = EM_ACTION.DELETE;
        pack = EMEngine.addRemoveEM(existVO, pack);
        updatePile.push(existVO);
      } else { // existe et maintenu, c'est un update possible
        existVO.operation = EM_ACTION.UPDATE;
        // console.log('merge', toUniqueMap.get(existVO.uniqueKey), existVO);
        pack = EMEngine.addMergeEM(toUniqueMap.get(existVO.code), existVO, pack);
        updatePile.push(existVO);
      }
    });

    [...toUniqueMap.values()].forEach(newBe => {
      if(!fromUniqueMap.has(newBe.code)) { // on ajoute les creations
        newBe.operation = EM_ACTION.CREATE;
        pack = EMEngine.addCreationEM(newBe, pack);
        updatePile.push(newBe);
      } 
    });


    if (pack && pack.entries.length > 0) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), updatePile, callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(); // retour en vue
    }
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // les VO source
    const vos: ScenarioVO[] = cmd.sourceVO as ScenarioVO[];

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
      cmd.viewCallBack(vos, cmd.state); // retour en vue
      return;
    } 
    
    // le statut du pack
    const packStatus: string = cmd.result.status;
    // la liste d'erreurs métier
    const errors: GenericException[] = cmd.result.errors; 
    if (packStatus === WIZ_COMMAND_STATE.FAULT) {
      errors.forEach((exp: GenericException) => {
        WizPopUp.toast(exp.message, WizIcon.TOAST_ERROR).show();
      });
      cmd.viewCallBack(vos, packStatus); // retour en vue
      return;
    } 
  
    // console.log('cmd result', cmd.result);
      
    // on prépare les clones en map
    const clonesMap: Map<string, ScenarioVO> = new Map(vos.map(item => [item.code, item]));
    let operation: string;
    let bddScen: Scenario;


    let cloneScen: ScenarioVO; // la correspondance dans le tableau de clones
    
    cmd.result.entries.forEach((emp: EMPackEntry) =>{
      // console.log('emp', emp);
      // on prend les données du packEntry
      operation = emp.operation;
      bddScen = emp.entity as Scenario;

      // le clone 
      cloneScen = clonesMap.get(bddScen.code); // va être nul en cas d'opération DELETE

      switch (operation) {
        case EM_ACTION.DELETE:
          // simple suppression dans le modèle, les clones sont OK
          this.removeScenario(bddScen.visionCode, bddScen.code);
          break;
        case EM_ACTION.UPDATE:
          cloneScen.source = bddScen; // le clone mis à jour
          this.getScenario(bddScen.visionCode, bddScen.code).source = bddScen;
          break;
        case EM_ACTION.CREATE:
          cloneScen.source = bddScen; // le clone mis à jour
          this.addScenario(cloneScen);
          break;

      }
      
    });
    cmd.viewCallBack(vos, packStatus); // retour en vue
  }


  /**
   * suppression de l'instance d'un Scenario
   * @param visionCode la vision de rattachement de ce Scenario
   * @param uniqueKey: Le code # level du Scenario
   */
  private removeScenario(visionCode: string, uniqueKey: string): void {
    const toDie: ScenarioVO = this.getScenario(visionCode, uniqueKey);
    this.model.scenariiList.splice(this.model.scenariiList.indexOf(toDie), 1);
    this.model.scenariiMap.get(visionCode).delete(uniqueKey);
  }

  /**
   * ajout de l'instance d'un Scenario
   * @param vo ScenarioVO
   */
  private addScenario(vo: ScenarioVO): void {
    this.model.scenariiList.push(vo);
    if (!this.model.scenariiMap.has(vo.visionCode)) {
      this.model.scenariiMap.set(vo.visionCode, new Map<string, ScenarioVO>());
    }
    this.model.scenariiMap.get(vo.visionCode).set(vo.code, vo);
  }

  /**
   * recherche de la pile d'enfants d'un scenar (pour delete)
   * @param vo ScenarioVO
   * @param addDaddy, on ajoute papa à la pile
   */
  public getAllChildren(daddy: ScenarioVO, addDaddy: boolean = false): Map<string, ScenarioVO> {
    let result: Map<string, ScenarioVO> = null;

    if(addDaddy) {
      result = new Map();
      result.set(daddy.code, daddy);
    }

    let visionScen: ScenarioVO[] = this.model.scenarioEngine.getScenarioByVision(daddy.visionCode);
    visionScen.forEach(scen => {
      while(!!scen?.parentCode && scen.parentCode !== ViewData.SCEN_KEY_CODE) {
        if(scen.parentCode === daddy.code) {
          if(!result) {
            result = new Map();
          }
          result.set(scen.code, scen);
        }
        scen = this.getScenario(daddy.visionCode, scen.parentCode);
      }
    });
    return result;
  }

  /**
   * recherche la présence de lignes / values pour le scenar
   * @param vo ScenarioVO
   */
  public hasLines(vo: ScenarioVO): boolean {
    let nbLines: number = 0;
    let nbValues: number = 0;
    if(!!vo.stats) {
      vo.stats.lines.forEach(vls => {
        nbLines += vls.nbLines;
      });
      vo.stats.values.forEach(vvs => {
        nbValues += vvs.nbLines;
      });
    }

    return nbLines + nbValues > 0;
  }

  /**
   * retourne les scenarii disponibles pour un widget / FEI
   * @param visionCode code de la vision
   * @param level step cible
   */
  public getScenarioByVisionAndLevel(visionCode: string, level: number): ScenarioVO[] {
    
    let result: ScenarioVO[] = [];
    // // TODO contrôler que le scenario est visible pour ce step
    let allScens: ScenarioVO[] = this.getScenarioByVision(visionCode);
    allScens.forEach(scen => {
      let stepParam = this.getStepParameters(scen, level);
      if(!!stepParam?.visible) {
        result.push(scen);
      }
    });

    return result;
  }

  /** clonage de la pile de scenarii en cours, pour travailler sur une collection de clones */
  public cloneAll(scens: ScenarioVO[]): ScenarioVO[] {
    if(scens?.length > 0) {
      return cloneDeep(scens);
    }
    return [];
  }

  /** 
   * génère une entrée MAIN pour la vision
   * @param visionCode 
   * @returns 
   */
  public override generateMain(visionCode: string): ScenarioVO {
    const scen: ScenarioVO = new ScenarioVO(this.model);
    scen.domain = this.model.domainCode;
    scen.businessUnitCode = this.model.businessUnitCode;
    scen.visionCode = visionCode;
    scen.parentCode = null;
    scen.code = ViewData.DEFAULT_SCENARIO;
    let entry: TranslatableLabelEntryVO = new TranslatableLabelEntryVO();
    entry.languageCode = this.model.buLocaleCode;
    entry.label = WizTranslationManager.getInstance().translateKey('$.scen.main.$');
    scen.labelVO.addEntry(entry);
    const currentLanguage: string = this.model.currentLocaleCode;
    if (currentLanguage !== entry.languageCode) {
      entry = new TranslatableLabelEntryVO();
      entry.languageCode = currentLanguage;
      entry.label = WizTranslationManager.getInstance().translateKey('$.scen.main.$');
      scen.labelVO.addEntry(entry);
    }
    return scen;
  }

  /** 
   * créarion d'un scénario enfant
   * @param vision cible
   * @param parent éventuel
   * @returns new scenarioVO
   */
  public createChildScen(vision: VisionVO, parent: ScenarioVO, currentPile: ScenarioVO[]): ScenarioVO {
    const result: ScenarioVO = new ScenarioVO(this.model); 
    result.domain = this.model.domainCode;
    result.businessUnitCode = this.model.businessUnitCode;
    result.visionCode = vision.code;
    result.parentCode = parent?.code || ViewData.DEFAULT_SCENARIO;

    let allMap: Map<string, ScenarioVO> = new Map(currentPile.map(item=> [item.code, item])); // pour unicité du code

    let codePrecursor: string = parent ? parent.code : vision.code;

    let newIdx: number = allMap.size + 1;
    let newCode: string = [codePrecursor, newIdx].join(ViewData.NICE_KEY_SEPARATOR);
    while(allMap.has(newCode)) {
      newIdx++;
      newCode = [codePrecursor, newIdx].join(ViewData.NICE_KEY_SEPARATOR);
    }
    result.code = newCode;
    result.initCode = newCode; // mémorisation du code d'origine, il sert pour l'identification dans l'arbre

    // Libellés
    let entry: TranslatableLabelEntryVO = new TranslatableLabelEntryVO();
    entry.languageCode = this.model.buLocaleCode;
    entry.label = newCode;
    result.labelVO.addEntry(entry);
    const currentLanguage: string = this.model.currentLocaleCode;
    if (currentLanguage !== entry.languageCode) {
      entry = new TranslatableLabelEntryVO();
      entry.languageCode = currentLanguage;
      entry.label = newCode;
      result.labelVO.addEntry(entry);
    }
    return result;
  }

  /** 
   * sur affichage d'un scenar, on s'assure qu'il contient ce qu'il faut en terme de conf par step
   * 
   * @param scen cible
   * @param vision d'appartenance pour collecte des steps
   */
  public initParameters(scen: ScenarioVO, vision: VisionVO): void {
    
    let scenParams: ScenarioParameters = scen.parameters;
    if(!scenParams.color) {
      scenParams.color = StyleEngine.STYLE_DEFAULT_SCEN_COLOR;
    }

    // l'existant.
    let stepParams: ScenarioStepParameter [] = scenParams.stepParameters || [];
    let stepParamsMap: Map<number, ScenarioStepParameter> = new Map(stepParams.map(item => [item.level, item]));

    let visionMaxStep: number = Object.values(vision.visionSteps).length;
    for(let i: number = 1; i<= visionMaxStep; i++) {
      if(!stepParamsMap.has(i)) {
        stepParams.push({level: i, editable: true, visible: true});
      }
    }
    scenParams.stepParameters = stepParams;
    scen.parameters = scenParams;
  }

  /** retourne la conf de scen pour le step donné */
  public getStepParameters(scenario: ScenarioVO, level: number): ScenarioStepParameter {
    let res: ScenarioStepParameter[] = scenario.stepParameters?.filter(item => item.level === level);
    if(res.length === 1) {
      return res[0];
    }
    return null;
  }

  /** 
   * dessin des coches dans le rendu éditables
   * 
   * @param scenColor couleur de la coche
   * @param multiPart rendu 2 parts sur pivot

   * @returns 
   */
  public writeScenPart(scenColor: string, multiPart: boolean): string {
    let result: string = '';
    if(multiPart) {
      result =  `<div style="position: absolute; width: 30px; height: 30px; 
      background-color: ${scenColor} !important; 
      transform: rotate(45deg); right: -14px; top: -22px;" ></div>`;
      result +=  `<div style="position: absolute; width: 30px; height: 30px; 
      background-color: white; 
      transform: rotate(45deg); right:-15px; top: -25px;" ></div>`;
    } else {
      result =  `<div style="position: absolute; width: 30px; height: 30px; 
      background-color: ${scenColor} !important; 
      transform: rotate(45deg); right: -15px; top: -25px;" ></div>`;
    }
    return result;
  }

  /**
   * détection de la présence de scénario depuis les params de cellule
   * @param params ICellRendererParams
   * @returns scenCellConfig
   */
  public revertScenDetection(params: any) : scenCellConfig {
    let result: scenCellConfig = {};

    result.isScen = false;
    result.hasDefault = false;
    if(params.colDef?.fkd?.code === ViewData.REVERT_MESURE_COLUMN) { // c'est la 1ère colonne, elle porte le nom des mesures
      return result;
    }

    const line: WidgetLine = params.data; // ligne source (la mesure en fait)
    const fkd: FeiKeyDef = params.colDef.fkd; // fkd (une mesure fake, c'est une fait un code FDV ou REVERT_MESURE_COLUMN)
    // prise de la vision
    let visionCode: string = params.colDef.widget?.vision?.code;

    // prise de la vraie ligne
    let srcCol: WizColDef =  params.colDef.widget?.columnsMap?.get(fkd.code);
    let realLine: WidgetLine = srcCol?.widgetLine;
    if(realLine) { // pas forcément sur modif de filtre
      let realLinePack: WidgetLine[] = [...realLine.ch?.values() || []];
      if(realLinePack?.length > 0) {
        let scenMap: Set<string> = new Set();
        realLinePack.forEach((wl: WidgetLine) => {
          let cell: WidgetCell = wl?.cells?.get(line?.fkd?.fullCode);
          if(!!cell) {
            scenMap.add(cell.sc);
          }
        });
        // console.log('scenMap', scenMap);
        scenMap.delete(null);
        scenMap.delete(undefined);

        [...scenMap.keys()].forEach(scen => {
          if(scen !== ViewData.DEFAULT_SCENARIO) {
            result.isScen = true;
            result.color = this.getScenario(visionCode, scen)?.color;
          }  else {
            result.hasDefault = true;
          }
        });
      }
    }


    return result;
  }


  /**
   * détection de la présence de scénario depuis les params de cellule
   * @param params ICellRendererParams
   * @returns scenCellConfig
   */
  public baseScenDetection(params: any) : scenCellConfig {

    let result: scenCellConfig = {};

    result.isScen = false;
    result.hasDefault = false;
    
    // console.log('baseScenDetection', params);

    // définition de la colonne
    let fkd: FeiKeyDef = params.colDef.fkd;
    // prise de la vision
    let visionCode: string = params.colDef.widget?.vision?.code;
    // prise du fei
    let fei: FeiVO = params.colDef.widget?.fei;

    // prise de la ligne
    let widgetLine: WidgetLine = params.data;

    result.useScen = !!params.colDef.widget?.context?.scenOn;
    result.color = StyleEngine.STYLE_DEFAULT_SCEN_COLOR;

    if(result.useScen) { 
      if(fkd.isFilterKey) { // pour les filters, tester les trios vs ligne de ref
        // console.log('baseScenDetection filter', params);
        // TODO sur pivot... trouver plus efficace
        let bl: Baseline = widgetLine.bl;    
        if(!!bl && bl.scen === widgetLine.scen) {
          result.isScen = true;
          result.color = this.getScenario(visionCode, widgetLine.scen)?.color;
        }

      } else if(fkd.editable) {   // une mesure éditable
        let col: any = params.columnApi.getColumn(params.colDef)?.parent;
        // console.log('cocol', col.colId, params.colDef);
  
        let line: WidgetLine = params.data; // l'entrée de ligne    
        let realLinePack: WidgetLine[] = []; // le pool qu'on va gérer, si on est en mode pivot
        
        if(!!line.linesByColumn?.has(col)) { // on est en pivot, on prend seulement les lignes éligibles
          realLinePack = line.linesByColumn.get(col);
        } else {
          realLinePack = [line];
        }

        // console.log('line', line);

        if(realLinePack?.length > 0) {
          let scenMap: Set<string> = new Set();

          let cell: WidgetCell;
          realLinePack.forEach((wl: WidgetLine) => {

            if(wl.ch?.size > 0) { // mode pivot, faut prendre les enfants
              [...wl.ch.values()].forEach(sub => {
                cell = sub?.cells?.get(fkd.fullCode);
                if(!!cell) {
                  scenMap.add(cell.sc);
                }
              });
            } else {
              cell = wl?.cells?.get(fkd.fullCode);
              if(!!cell) {
                scenMap.add(cell.sc);
              }
            }

          });
          // console.log('scenMap', scenMap);
          scenMap.delete(null);
          scenMap.delete(undefined);
  
          [...scenMap.keys()].forEach(scen => {
            if(scen !== ViewData.DEFAULT_SCENARIO) {
              result.isScen = true;
              result.color = this.getScenario(visionCode, scen)?.color;
            }  else {
              result.hasDefault = true;
            }
          });
        }
      }
    }
    return result;

  }


  /**
   * return true si existScen > targetScen (donc targetScen dans la pile de parent de existScen)
   * @param targetScen scenario cible
   * @param existScen scenario existant
   * @returns true si existScen <= targetScen
   */
  public isBetterScen(targetScen: string, existScen: string): boolean {
   
    if(targetScen === ViewData.DEFAULT_SCENARIO) { // si la cible est "MAIN", c'est forcément faux
      if(existScen === ViewData.DEFAULT_SCENARIO) { // ...sauf si l'existant, c'est MAIN
        return true;
      } else {
        return false;
      } 
    } else { // j'ai un scenar
      if(existScen === ViewData.DEFAULT_SCENARIO) { // c'est forcément mieux que MAIN
        return true;
      }
    }


    let me: ScenarioVO = this.getScenario(this.model.currentVision.code, targetScen);
    let itsDone: boolean = false;
    let result: boolean = false;
    let idx: number = 0;
    while(!itsDone || idx > 100) {
      if(!me || me.code === existScen) { // j'ai pas mieux
        result = true;
      }
      if(result || !me.parentCode || me.parentCode === ViewData.DEFAULT_SCENARIO) { // on a remonté toute la pile, on est à MAIN...
        itsDone = true;
      }
      if(!!me?.parentCode) {
        me = this.getScenario(this.model.currentVision.code, me.parentCode);
      }
      
      idx++;
      if(idx === 50) {
        console.log('wtf infinite loop', targetScen, existScen);
      }
    }
    return result;
  }

 /**
   * retourne vrai si existScen dépend de moi
   * @param targetScen scenario cible
   * @param existScen scenario existant
   * @returns true si existScen <= targetScen
   */
  public isMyChild(MeScen: string, existScen: string): boolean {
   
    if(MeScen === ViewData.DEFAULT_SCENARIO) { // tout le monde dépend du scénarion de base
        return true;
    }

    let realScen: ScenarioVO = this.getScenario(this.model.currentVision.code, existScen);
    let itsDone: boolean = false;
    let result: boolean = false;
    while(!!realScen?.parentCode && !itsDone) {
      if(realScen.code === MeScen) {
        itsDone = true;
        result = true;
      } else if(realScen?.parentCode === ViewData.DEFAULT_SCENARIO) {
        itsDone = true; // on est remonté à la racine
      } else {
        realScen = this.getScenario(this.model.currentVision.code, realScen.parentCode);
      }
    }
    return result;
  }

  /** retourne le scenario qui me précède, pour remplace de baseline dans le workingPool */
  public getPreviousScen(scen: string): string {
    let result: string = null;
    if(scen !== ViewData.DEFAULT_SCENARIO) { // pas de parent pour la base...
      let realScen: ScenarioVO = this.getScenario(this.model.currentVision.code, scen);
      if(!!realScen?.parentCode) {
        result = realScen.parentCode;
      }
    }

    return result;
  }

  /**
   * retour de service de suppression de scenarii
   * @param vision cible
   * @param scenarii morts
   */
  public removeScenarii(visionCode: string, scenarii: ScenarioVO[]): void {
    // console.log('removeScenarii', visionCode, scenarii);

    scenarii.forEach(scen => {
      this.removeScenario(scen.visionCode, scen.code);
    });
    if(this.model.currentVision?.code === visionCode) {
      this.model.widgetDataService.resetAll();
      this.model.dataEngine.clearFei(); // suppression des FEI chargés
      this.model.cellMatrixEngine.clear(); // rincer la matrice de cellules
    }
  }

}
